import { Input } from '@invent/wl-ui-kit-next/ui';
import { X } from 'lucide-react';
import * as React from 'react';

/**
 * A list of values typed one at a time — a condition's "is one of" and "mentions any of" (SR-227,
 * R9, design `docs/superpowers/specs/2026-10-05-concern-criteria-builder-ui.md` §3.3).
 *
 * Each value is a chip with its own ✕, and the box after them takes the next: Enter or a comma
 * adds what was typed, trimmed; Backspace in an empty box takes the last chip back. **Leaving the
 * box adds what it holds** too — a person who types "Settled" and presses Save meant it, and a
 * box that dropped it would send `[]` with nothing said. Nothing is judged: an empty entry is
 * simply not a value, and a repeated one is kept (survey-api's parser takes the list as sent, and
 * a word twice changes nothing it tests). The rejected alternative was one text box split on
 * commas, which hides where one value ends and the next begins — a chip shows each value exactly
 * as it will be compared.
 *
 * The admin domain's: the criteria builder's and the list filter's *Advanced…* (SR-230) — both
 * admin screens, so not the design system's.
 */
export function TagInput({
  label,
  values,
  onChange,
  listId,
  invalid
}: {
  /** The group's accessible name — the row's "Condition n — Value". */
  label: string;
  values: readonly string[];
  onChange: (values: string[]) => void;
  /** A `datalist` the box offers, where the caller has values to suggest. */
  listId?: string;
  invalid?: boolean;
}) {
  const [draft, setDraft] = React.useState('');

  const add = () => {
    const value = draft.trim();
    setDraft('');
    if (value !== '') onChange([...values, value]);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      // Enter would otherwise submit the form, and a comma is the separator, not a character.
      event.preventDefault();
      add();
    } else if (event.key === 'Backspace' && draft === '' && values.length > 0) {
      event.preventDefault();
      onChange(values.slice(0, -1));
    }
  };

  return (
    <div
      role="group"
      aria-label={label}
      className={
        'flex min-h-9 w-72 flex-wrap items-center gap-1 rounded-md border bg-white px-1.5 py-1 ' +
        (invalid ? 'border-destructive' : 'border-border')
      }
    >
      {values.map((value, index) => (
        <span
          key={index}
          className="inline-flex items-center gap-1 rounded bg-[var(--color-off-white)] py-0.5 pr-0.5 pl-1.5"
        >
          {value}
          <button
            type="button"
            aria-label={`Remove ${value}`}
            className="grid size-4 place-items-center rounded text-muted-foreground hover:text-foreground"
            onClick={() => onChange(values.filter((_, at) => at !== index))}
          >
            <X className="size-3" aria-hidden />
          </button>
        </span>
      ))}
      <Input
        aria-label="Add a value"
        value={draft}
        list={listId}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={onKeyDown}
        onBlur={add}
        className="h-7 min-w-24 flex-1 border-0 px-1 shadow-none focus-visible:ring-0"
        autoComplete="off"
      />
    </div>
  );
}
